import type { ExpressionSpecification } from 'mapbox-gl';
import { useIntl } from 'react-intl';
import { FONT_SIZE_100 } from '@alltrails/denali/tokens';
import { useAppSelector } from '../../redux';
import type { TrailResult } from '../../redux/reducer';
import {
  activePillTextColor,
  buildTrailheadPillIconImage,
  buildTrailheadPillLabelExpression,
  buildTrailheadPillNameTextOffset,
  buildTrailheadPillTextOffset
} from '../../utils/buildTrailheadPillExpressions';
import { getTrailheadPinStyle } from '../../utils/trailheadPinVariant';
import TrailheadPinLayers, { type TrailheadPinLayerProps } from '../TrailheadPinLayers/TrailheadPinLayers';

type TrailheadVectorPillPinLayerProps = TrailheadPinLayerProps & {
  trailsByTrailhead: Map<number, TrailResult[]>;
};

/**
 * The pill pins: a pill with the trail length or count. A hovered or selected pill inverts its colors, and a collapsed
 * pin expands to the same pill.
 */
const TrailheadVectorPillPinLayer = ({
  isHoveredExpression,
  isSelectedExpression,
  pinVariant,
  trailsByTrailhead,
  ...layerProps
}: TrailheadVectorPillPinLayerProps) => {
  const intl = useIntl();
  const displayMetric = useAppSelector(state => state.context.displayMetric);
  const variantExpressions = {
    isClosedExpression: layerProps.isClosedExpression,
    isCompletedExpression: layerProps.isCompletedExpression,
    isSavedExpression: layerProps.isSavedExpression
  };
  const isActiveExpression: ExpressionSpecification = ['any', isHoveredExpression, isSelectedExpression];
  // The icon stretches to the label's width, at a fixed size
  const pillLayout = {
    'icon-image': buildTrailheadPillIconImage(variantExpressions, isActiveExpression, getTrailheadPinStyle(pinVariant)),
    'icon-size': 1,
    'icon-text-fit': 'width',
    'text-field': buildTrailheadPillLabelExpression(trailsByTrailhead, { displayMetric, intl }),
    'text-offset': buildTrailheadPillTextOffset(),
    'text-size': FONT_SIZE_100
  } as const;

  return (
    <TrailheadPinLayers
      {...layerProps}
      activeTextColor={activePillTextColor}
      collapsedActiveLayout={pillLayout}
      expandedLayout={pillLayout}
      isActiveExpression={isActiveExpression}
      nameTextOffset={buildTrailheadPillNameTextOffset()}
    />
  );
};

export default TrailheadVectorPillPinLayer;
